import { Controller } from "@hotwired/stimulus";

interface RatioConfig {
  icon: string;
  label: string;
  aspectClass: string;
}

const RATIO_CONFIGS: Record<string, RatioConfig> = {
  "auto": {
    icon: '<div class="w-4 h-4 border-2 border-dashed border-current rounded-sm"></div>',
    label: "auto",
    aspectClass: "aspect-square",
  },
  "1:1": {
    icon: '<div class="w-4 h-4 border-2 border-current rounded-sm"></div>',
    label: "1:1",
    aspectClass: "aspect-square",
  },
  "16:9": {
    icon: '<div class="w-5 h-3 border-2 border-current rounded-sm"></div>',
    label: "16:9",
    aspectClass: "aspect-video",
  },
  "9:16": {
    icon: '<div class="w-3 h-5 border-2 border-current rounded-sm"></div>',
    label: "9:16",
    aspectClass: "aspect-[9/16]",
  },
  "4:3": {
    icon: '<div class="border-2 border-current rounded-sm" style="width: 18px; height: 14px;"></div>',
    label: "4:3",
    aspectClass: "aspect-[4/3]",
  },
  "3:4": {
    icon: '<div class="border-2 border-current rounded-sm" style="width: 14px; height: 18px;"></div>',
    label: "3:4",
    aspectClass: "aspect-[3/4]",
  },
  "21:9": {
    icon: '<div class="w-6 h-2.5 border-2 border-current rounded-sm"></div>',
    label: "21:9",
    aspectClass: "aspect-[21/9]",
  },
};

export default class AspectRatioPickerController extends Controller {
  static targets = ["trigger", "menu", "currentIcon", "currentLabel", "input", "coverage"];
  static values = {
    supportMap: { type: Object, default: {} },
    totalModels: { type: Number, default: 0 },
  };

  declare triggerTarget: HTMLButtonElement;
  declare menuTarget: HTMLUListElement;
  declare currentIconTarget: HTMLElement;
  declare currentLabelTarget: HTMLElement;
  declare inputTarget: HTMLInputElement;
  declare coverageTarget: HTMLElement;
  declare hasCoverageTarget: boolean;
  declare supportMapValue: Record<string, number>;
  declare totalModelsValue: number;

  private currentRatio = "1:1";

  connect(): void {
    // Sync internal state with server-rendered default
    this.currentRatio = this.inputTarget.value || "1:1";
    // Close menu when clicking outside
    document.addEventListener("click", this.handleOutsideClick.bind(this));
    // Listen for programmatic value changes (e.g. localStorage restore)
    this.inputTarget.addEventListener("change", this.syncFromInput.bind(this));
    this.updateCoverage();
  }

  disconnect(): void {
    document.removeEventListener("click", this.handleOutsideClick.bind(this));
  }

  select(event: Event): void {
    const button = event.currentTarget as HTMLButtonElement;
    const ratio = button.dataset.ratio;
    if (!ratio) return;

    // Fall back to a generic config for ratios we don't have explicit
    // styling for (e.g. "auto" or unusual provider-specific ratios) so
    // selection still updates the label, hidden input, and coverage line.
    const config = RATIO_CONFIGS[ratio] || {
      icon: '<div class="w-4 h-4 border-2 border-current rounded-sm"></div>',
      label: ratio,
      aspectClass: "aspect-square",
    };

    this.currentRatio = ratio;
    this.currentIconTarget.innerHTML = config.icon;
    this.currentLabelTarget.textContent = config.label;
    this.inputTarget.value = ratio;
    // The hidden input wires `change->playground#onAspectRatioChange`, which
    // reshapes every panel container (and clears any leftover inline size-tab
    // aspect-ratio). No need to touch the containers here too — doing so used
    // to double-update and, worse, left a stale inline `aspect-ratio` style in
    // place that overrode the new class.
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
    this.updateCoverage();
    this.closeMenu();
  }

  /** Sync the picker display when the hidden input is changed programmatically. */
  private syncFromInput(): void {
    const ratio = this.inputTarget.value;
    if (!ratio || ratio === this.currentRatio) return;
    const config = RATIO_CONFIGS[ratio] || {
      icon: '<div class="w-4 h-4 border-2 border-current rounded-sm"></div>',
      label: ratio,
      aspectClass: "aspect-square",
    };
    this.currentRatio = ratio;
    this.currentIconTarget.innerHTML = config.icon;
    this.currentLabelTarget.textContent = config.label;
    this.updateCoverage();
  }

  /**
   * Refresh the "Supported by N of M models" line based on the active ratio.
   * No-op when there's no coverage element (e.g. legacy callers that didn't
   * pass support_map into the partial).
   */
  private updateCoverage(): void {
    if (!this.hasCoverageTarget) return;
    const total = this.totalModelsValue;
    if (total <= 0) return;
    const count = this.supportMapValue[this.currentRatio];
    const supported = typeof count === "number" ? count : total;
    if (supported >= total) {
      this.coverageTarget.textContent =
        `Supported by all ${total} selected model${total === 1 ? "" : "s"}`;
    } else {
      this.coverageTarget.textContent =
        `Supported by ${supported} of ${total} — others fall back to their closest supported ratio`;
    }
  }

  private handleOutsideClick(event: Event): void {
    if (!this.element.contains(event.target as Node)) {
      this.closeMenu();
    }
  }

  private closeMenu(): void {
    // Only blur the dropdown trigger/menu elements, not other focused elements like the prompt textarea
    if (this.element.contains(document.activeElement)) {
      (document.activeElement as HTMLElement)?.blur();
    }
  }
}


